'use client';

import { Typography } from '@mui/material';
import type { SxProps, Theme, TypographyProps } from '@mui/material';
import { forwardRef } from 'react';
import type { HTMLAttributes, MouseEventHandler } from 'react';

export const ClickableText = forwardRef<
  HTMLElement,
  Omit<
    HTMLAttributes<HTMLElement>,
    'children' | 'color' | 'onClick' | 'style'
  > & {
    children: React.ReactNode;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    variant?: TypographyProps['variant'];
    noWrap?: boolean;
    title?: string;
    className?: string;
    sx?: SxProps<Theme>;
    component?: 'span';
    /**
     * Overrides the base text color. Defaults to the variant's natural color.
     * Use for semantic color contexts (e.g. error-main for threat-state text).
     */
    baseColor?: string;
    /**
     * Overrides the hover text color.
     * Defaults to var(--mui-palette-text-secondary).
     */
    hoverColor?: string;
    'data-testid'?: string;
  }
>(
  (
    {
      children,
      onClick,
      variant,
      noWrap,
      title,
      className,
      sx,
      component,
      baseColor,
      hoverColor,
      'data-testid': dataTestId,
      ...rest
    },
    ref
  ) => {
    const colorSx = {
      ...(baseColor && { color: `${baseColor} !important` }),
    };
    const consumerSx = Array.isArray(sx) ? sx : [sx];
    const componentProps = component ? { component } : {};
    const setElementRef = (element: HTMLElement | null) => {
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    if (!onClick) {
      return (
        <Typography
          {...rest}
          {...componentProps}
          ref={setElementRef}
          variant={variant}
          noWrap={noWrap}
          title={title}
          data-testid={dataTestId}
          className={className}
          sx={[colorSx, ...consumerSx]}
        >
          {children}
        </Typography>
      );
    }

    return (
      <Typography
        {...rest}
        ref={setElementRef}
        component='button'
        type='button'
        variant={variant}
        noWrap={noWrap}
        title={title}
        data-testid={dataTestId}
        onClick={onClick}
        className={className}
        sx={[
          {
            border: 'none',
            background: 'none',
            padding: '0px',
            cursor: 'pointer',
            textAlign: 'inherit',
            textDecoration: 'underline',
            ...colorSx,
            '&:hover': {
              color: hoverColor
                ? `${hoverColor} !important`
                : 'var(--mui-palette-text-secondary)',
            },
          },
          ...consumerSx,
        ]}
      >
        {children}
      </Typography>
    );
  }
);

ClickableText.displayName = 'ClickableText';
